import { serviceName, type Language } from './kioskText'
import type { CallPageSettings, Service, Ticket } from './types'

/**
 * The call page: a hall board giving a fresh call the whole screen.
 *
 * A queue rather than "show the latest", because a busy branch calls several
 * numbers inside a few seconds and the obvious implementation — replace whatever
 * is showing — gives the first customer's number a quarter of a second on the wall.
 * They were looking at their phone. The board moved on without them.
 *
 * The rules, and why:
 * - a call keeps the screen for its full time while nothing waits;
 * - while something waits, the call showing keeps it for four seconds from when it
 *   appeared, never less, so the page changes at most once every four seconds —
 *   about as fast as a hall can read a number and look for its desk;
 * - at most three wait. A fourth drops the oldest waiting call, never the one
 *   showing. A dropped call is still on the calls grid underneath, which is the
 *   real announcement, and it is back in view the moment the burst passes;
 * - the same announcement twice is shown once; a recall is a new announcement,
 *   because the customer did not come. That dedupe (ticketId:at) is the only
 *   thing between the board and a call shown twice, and it holds because the page
 *   is fed from LiveConnection's onAnnounce alone, which tells a call once — never
 *   from snapshots or deltas read some other way;
 * - no call holds the screen for less than four seconds, whatever it is told: the
 *   choices start at four and the server refuses less.
 *
 * Paced by its own clock and not by the voice (lib/voice.ts). A board whose
 * browser has blocked sound, or that has no voice at all, must still move on. The
 * two queues are both first in, first out and both drop the oldest, so in a burst
 * they stay roughly in step.
 *
 * Pure: every function takes the time it is told, so the rules are tested here
 * rather than on a wall.
 *
 * A design's Called screen (layout/calledScreen.ts) runs on the same queue with its
 * own rules (CALLED_SCREEN_RULES): every call gets its own turn at its full time
 * until three wait, then three seconds each; past ten waiting the oldest waiting
 * call is dropped (it is still on the board's list).
 */

export const CALL_PAGE_DEFAULT_SECONDS = 8
export const CALL_PAGE_SECONDS_CHOICES: readonly number[] = [4, 6, 8, 10, 15, 20, 30]
export const BUSY_SECONDS = 4
export const MAX_WAITING = 3

/** How a queue paces its calls. */
export interface QueueRules {
  /** No call shows for less, whatever it is told. */
  minSeconds: number
  /** A call's time while at least busyFrom calls wait. */
  busySeconds: number
  busyFrom: number
  /** More waiting than this drops the oldest waiting call. */
  maxWaiting: number
}

/** The machine's call page: four seconds once anything waits, at most three waiting. */
export const CALL_PAGE_RULES: QueueRules = { minSeconds: BUSY_SECONDS, busySeconds: BUSY_SECONDS, busyFrom: 1, maxWaiting: MAX_WAITING }

/** A design's Called screen: each call its full time until three wait, then three seconds each. */
export const CALLED_SCREEN_RULES: QueueRules = { minSeconds: 3, busySeconds: 3, busyFrom: 3, maxWaiting: 10 }

export interface TakeoverCall {
  /** ticketId:at — one announcement. */
  key: string
  number: string
  desk: string
  /** Who and where, for a Called screen's bindings, looked up when it is drawn. */
  ticketId?: string
  counterId?: string
  /** Read out when the call takes the screen, not when it arrives (a Called screen's). */
  sound?: boolean
}

export interface TakeoverState {
  showing: TakeoverCall | null
  /** When the call showing appeared, in milliseconds. */
  since: number
  waiting: TakeoverCall[]
}

export const IDLE: TakeoverState = { showing: null, since: 0, waiting: [] }

/** Off unless a machine has been set to it. The same rule org.CallPageSettings keeps. */
export function showsCallPage(s: CallPageSettings | undefined): boolean {
  return s?.show === true
}

/** The hold, the default when unset or outside what the server allows: a whole
 *  number of seconds from 4 to 30. */
export function callPageSeconds(s: CallPageSettings | undefined): number {
  const n = s?.seconds ?? 0
  return Number.isInteger(n) && n >= BUSY_SECONDS && n <= 30 ? n : CALL_PAGE_DEFAULT_SECONDS
}

/** A call's full time, never under the rules' least. */
function fullHold(seconds: number, rules: QueueRules): number {
  return Math.max(rules.minSeconds, seconds)
}

/** When the call showing gives up the screen: its full time, or four seconds
 *  while something waits. Zero when nothing is showing. */
export function holdUntil(state: TakeoverState, seconds: number, rules: QueueRules = CALL_PAGE_RULES): number {
  if (!state.showing) return 0
  const full = fullHold(seconds, rules)
  const hold = state.waiting.length >= rules.busyFrom ? Math.min(full, rules.busySeconds) : full
  return state.since + hold * 1000
}

export function enqueueCall(
  state: TakeoverState,
  call: TakeoverCall,
  now: number,
  seconds: number,
  rules: QueueRules = CALL_PAGE_RULES,
): TakeoverState {
  if (state.showing?.key === call.key || state.waiting.some((w) => w.key === call.key)) return state
  if (!state.showing) return { showing: call, since: now, waiting: [] }
  const waiting = [...state.waiting, call]
  if (waiting.length > rules.maxWaiting) waiting.splice(0, waiting.length - rules.maxWaiting)
  // The one showing may already have had its busy time; if so it goes now.
  return advanceCall({ ...state, waiting }, now, seconds, rules)
}

export function advanceCall(state: TakeoverState, now: number, seconds: number, rules: QueueRules = CALL_PAGE_RULES): TakeoverState {
  if (!state.showing || now < holdUntil(state, seconds, rules)) return state
  const [next, ...rest] = state.waiting
  return next ? { showing: next, since: now, waiting: rest } : IDLE
}

/** The call to read out now: the one that just took the screen, once, when it asks for sound. */
export function speakNow(state: TakeoverState, spoken: string): TakeoverCall | null {
  const s = state.showing
  return s && s.key !== spoken && s.sound ? s : null
}

/** When the board should look at the queue again, or null when nothing is up. */
export function nextWake(state: TakeoverState, seconds: number, rules: QueueRules = CALL_PAGE_RULES): number | null {
  return state.showing ? holdUntil(state, seconds, rules) : null
}

/**
 * How long the board's timer waits before its wake, or null when nothing is up.
 *
 * Never longer than a call's full hold. A clock stepped back (a correction from
 * the network, a machine that booted with the wrong time) puts the moment the
 * call appeared in the future, and a timer armed by that clock would wait for as
 * long as the step. The hold is never longer than a call's full time, so neither
 * is this.
 */
export function wakeDelay(state: TakeoverState, seconds: number, now: number, rules: QueueRules = CALL_PAGE_RULES): number | null {
  const wake = nextWake(state, seconds, rules)
  if (wake === null) return null
  return Math.min(Math.max(0, wake - now), fullHold(seconds, rules) * 1000)
}

/**
 * What the board's timer does when it fires: the call it was armed for gives up
 * the screen, whatever the clock says.
 *
 * Not advanceCall, which asks the clock. A timer can fire a moment early, or the
 * clock can have stepped back since it was armed; either way advanceCall answers
 * the same state, React keeps it, nothing arms another timer, and the number
 * stays on the wall all night. The timer was armed for the right moment, so its
 * firing is the moment.
 *
 * `armedFor` is the key the timer was armed for. When another call has taken the
 * screen since, that call keeps its own time and only the clock decides.
 */
export function wakeCall(
  state: TakeoverState,
  armedFor: string,
  now: number,
  seconds: number,
  rules: QueueRules = CALL_PAGE_RULES,
): TakeoverState {
  if (state.showing?.key !== armedFor) return advanceCall(state, now, seconds, rules)
  const [next, ...rest] = state.waiting
  return next ? { showing: next, since: now, waiting: rest } : IDLE
}

/** A call's service in words: the ticket's current service, else its queue's name. */
export function callServiceWords(
  ticket: Pick<Ticket, 'transactions' | 'currentTransaction'> | undefined,
  queue: { name: Record<string, string>; code: string } | undefined,
  services: readonly Pick<Service, 'id' | 'name' | 'code'>[],
  lang: Language,
  orgDefault?: Language,
): string {
  // A screen's ticket carries only its current service, or none from an older server.
  const id = ticket?.transactions?.[ticket.currentTransaction]?.serviceId
  const named = id ? services.find((s) => s.id === id) : undefined
  const from = named ?? queue
  if (!from) return ''
  return serviceName(from.name, lang, from.code, orgDefault)
}
